import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ScNotificationModal } from './styled';
import { NotificationDto, NotificationSchema } from './schema';
import { TitleH2 } from 'components/styles/common';
import { capitalizeFirstLetter } from 'helpers/common';
import InputForm from 'components/elements/inputs';
import { hoursToString } from 'helpers/dayjs/formats';
import TextareaForm from 'components/elements/inputs/textarea';
import { Button } from 'components/elements/Button';
import { useUpdateNotificationMutation } from 'graphql/mutations/settings/notifications/__generated__/updateNotification.generated';
import { errorAlert, infoAlert } from 'helpers/alerts';
import { useCreateNotificationMutation } from '../../../graphql/mutations/settings/notifications/__generated__/createNotification.generated';
import { UpdateNotificationInput } from '../../../types.generated';
import SelectForm from '../../elements/inputs/select';
import {
  NotificationPosition,
  NotificationPositionMeeting,
} from '../../../helpers/enums/common';
import { getSiteTheme } from 'hooks/getSiteTheme';
import { Checkbox } from 'components/catalyst-elements/checkbox';

export interface NotificationModalProps {
  id?: string | null;
  type?: string | null;
  body?: string | null;
  smsBody?: string | null;
  timeToSendNotification?: string | null;
  positionToSendNotification?: string;
  positionMeetingToSendNotification?: string;
  replyEmail?: string | null;
  bccEmail?: string | null;
  name?: string | null;
  subject?: string | null;
  includeSms?: boolean;
  refetch: any;
  onClose: () => void;
  update?: (notification: UpdateNotificationInput) => void;
}

export const NotificationModal = forwardRef<
  HTMLDivElement,
  NotificationModalProps
>((props, ref) => {
  const [update] = useUpdateNotificationMutation();
  const [activeTab, setActiveTab] = useState('email');
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(props.includeSms || false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [emailSubject, setEmailSubject] = useState(props.subject || '');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [emailBody, setEmailBody] = useState(props.body || '');

  const {
    replyEmail,
    bccEmail,
    name,
    timeToSendNotification,
    subject,
    body,
    smsBody: propsSmsBody,
    type,
    id,
    onClose,
    refetch,
    positionToSendNotification,
    positionMeetingToSendNotification,
    includeSms,
  } = props;

  const typeName = type ? type : 'CUSTOM';
  const methods = useForm<NotificationDto>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    resolver: yupResolver(NotificationSchema),
    defaultValues: {
      recursive: true,
      replyEmail: replyEmail != null ? replyEmail : '',
      bccEmail: bccEmail != null ? bccEmail : '',
      name: name != null ? name : '',
      subject: subject != null ? subject : '',
      body: body != null ? body : '',
      smsBody: propsSmsBody != null ? propsSmsBody : '',
      days: timeToSendNotification != null ? +timeToSendNotification : 0,
      positionToSendNotification:
        positionToSendNotification as NotificationPosition,
      positionMeetingToSendNotification:
        positionMeetingToSendNotification as NotificationPositionMeeting,
      includeSms: includeSms || false,
    },
  });

  const {
    handleSubmit,
    setValue,
    watch,
    setFocus,
    reset,
    formState: { errors, isSubmitSuccessful },
  } = methods;

  const [create, { loading, error }] = useCreateNotificationMutation();
  const watchBody = watch('body');

  const setVariable = useCallback(
    (watchBody: string, value: string) => {
      setValue('body', `${watchBody}${value}`);
      setFocus('body');
    },
    [setFocus, setValue]
  );

  const onSubmit: SubmitHandler<NotificationDto> = (input) => {
    const data = {
      replyEmail: input.replyEmail,
      bccEmail: input.bccEmail,
      name: input.name,
      timeToSendNotification: hoursToString(input.days),
      subject: input.subject,
      body: input.body,
      smsBody: input.smsBody,
      includeSms: smsEnabled,
      positionToSendNotification: input.positionToSendNotification,
      positionMeetingToSendNotification:
        input.positionMeetingToSendNotification,
    } as const;

    const processedData = Object.entries(data).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: value === '' ? null : value,
      }),
      {} as typeof data
    );

    if (update != null && id != null) {
      update({
        variables: { input: { ...processedData, id, recursive: true } },
      }).then(({ data, errors }) => {
        if (data != null) {
          refetch();
          infoAlert('Notification updated!');
        } else if (errors != null) {
          errorAlert('Notification is not updated');
        }
      });
      onClose();
      return;
    }
    return create({
      variables: {
        input: processedData,
      },
    });
  };

  useEffect(() => {
    if (isSubmitSuccessful && !error && onClose != null) {
      infoAlert('Notification created!');
      refetch();
      setTimeout(() => onClose(), 0);
      reset();
    }
  }, [error, errors, isSubmitSuccessful, onClose, refetch, reset]);

  useEffect(() => {
    setValue('includeSms', smsEnabled);
  }, [smsEnabled, setValue]);

  const { isLight } = getSiteTheme();

  return (
    <ScNotificationModal
      className={`${
        isLight ? 'bg-white' : 'bg-slate-800'
      } max-w-2xl mx-auto p-6 rounded-lg shadow-md`}
      ref={ref}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TitleH2 className="text-xl font-semibold mb-4">
            {capitalizeFirstLetter(typeName.toLowerCase())}
          </TitleH2>

          {/* Shared Settings */}
          <div className="space-y-4">
            <div>
              <label className="block font-medium">Name</label>
              <InputForm<NotificationDto>
                id="name"
                disabled={loading}
                placeholder="Internal name (optional)"
                type="text"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block font-medium">Send time (days)</label>
                <InputForm<NotificationDto>
                  id="days"
                  disabled={loading}
                  placeholder=""
                  required
                  type="number"
                />
              </div>
              <div>
                <label className="block font-medium">When to send</label>
                <SelectForm<NotificationDto>
                  id="positionToSendNotification"
                  label=""
                  required
                >
                  {Object.values(NotificationPosition).map((value, index) => (
                    <option key={index} value={value}>
                      {value}
                    </option>
                  ))}
                </SelectForm>
              </div>
              <div>
                <label className="block font-medium">Based on</label>
                <SelectForm<NotificationDto>
                  id="positionMeetingToSendNotification"
                  label=""
                  required
                >
                  {Object.values(NotificationPositionMeeting).map(
                    (value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    )
                  )}
                </SelectForm>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-medium">Reply Email</label>
                <InputForm<NotificationDto>
                  id="replyEmail"
                  disabled={loading}
                  placeholder=""
                  type="email"
                />
              </div>
              <div>
                <label className="block font-medium">BCC Email</label>
                <InputForm<NotificationDto>
                  id="bccEmail"
                  disabled={loading}
                  placeholder=""
                  type="email"
                />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex mt-6 border-b">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'email'
                  ? 'border-b-2 border-black'
                  : 'text-gray-500'
              }`}
              onClick={() => setActiveTab('email')}
            >
              Email
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'sms'
                  ? 'border-b-2 border-black'
                  : 'text-gray-500'
              }`}
              onClick={() => setActiveTab('sms')}
            >
              SMS
            </button>
          </div>

          {/* Email Tab */}
          {activeTab === 'email' && (
            <div className="mt-4 space-y-4">
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={emailEnabled}
                  onChange={(checked) => setEmailEnabled(checked)}
                />
                Enable Email Notification
              </label>
              {emailEnabled ? (
                <>
                  <div>
                    <label className="block font-medium">Email Subject</label>
                    <InputForm<NotificationDto>
                      id="subject"
                      disabled={loading}
                      placeholder="Subject"
                      type="text"
                    />
                  </div>
                  <div>
                    <label className="block font-medium">Email Body</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {NotificationVariables.map((item, indx) => (
                        <div
                          className={`px-2 py-1 rounded-md text-sm cursor-pointer transition-colors ${
                            isLight
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-gray-700 text-white hover:bg-gray-600'
                          }`}
                          key={item.value}
                          onClick={() => setVariable(watchBody, item.value)}
                        >
                          {item.label}
                        </div>
                      ))}
                    </div>
                    <TextareaForm<NotificationDto>
                      id="body"
                      disabled={loading}
                      placeholder="Write your email content..."
                      required
                    />
                    <small className="text-gray-500">
                      Use tags like {'{FirstName}'}
                    </small>
                  </div>
                </>
              ) : (
                <div className="text-gray-500 italic">
                  Email notification is disabled.
                </div>
              )}
            </div>
          )}

          {/* SMS Tab */}
          {activeTab === 'sms' && (
            <div className="mt-4 space-y-4">
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={smsEnabled}
                  onChange={(checked) => setSmsEnabled(checked)}
                />
                Enable SMS Notification
              </label>
              {smsEnabled ? (
                <div>
                  <label className="block font-medium">SMS Body</label>
                  <TextareaForm<NotificationDto>
                    id="smsBody"
                    disabled={loading}
                    placeholder="Write your SMS content..."
                    maxLength={160}
                    rows={3}
                  />
                  <small className="text-gray-500">
                    {watch('smsBody')?.length || 0} / 160 characters
                  </small>
                </div>
              ) : (
                <div className="text-gray-500 italic">
                  SMS notification is disabled.
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 flex justify-end gap-4">
            <Button type="button" onClick={() => {}}>
              Preview
            </Button>
            <Button
              type="submit"
              disabled={Boolean(Object.keys(errors).length)}
              isLoading={loading}
            >
              {id != null ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </FormProvider>
    </ScNotificationModal>
  );
});

export const NotificationVariables = [
  {
    label: 'FirstName',
    value: '{{order.firstName}}',
  },
  {
    label: 'LastName',
    value: '{{order.lastName}}',
  },
  {
    label: 'ActivityClassStartDate',
    value: '{{formatDateTime activityClass.start}}',
  },
  {
    label: 'ActivityClassEndDate',
    value: '{{formatDateTime activityClass.end}}',
  },
  {
    label: 'MeetingDays',
    value: '{{> timelines}}',
  },
  {
    label: 'TeacherName',
    value: '{{teacher.name}}',
  },
  {
    label: 'LocationAddress',
    value: '{{location.address}}',
  },
  {
    label: 'OrganizationName',
    value: '{{activity.organization.name}}',
  },
];
